<template>
  <div
    class="relative flex grow items-start b px-3 form-input rounded-sm"
    :class="[
      disabled ? 'bg-grey-90' : 'bg-white',
      disabled
        ? 'c-grey-30 b-grey-60 forced-colors:bg-[GrayText]'
        : invalid && !focused ? 'c-red-30 b-red-30'
          : invalid && focused ? 'c-red-30 b-red-30 ring-1 ring-inset ring-red-30'
            : 'b-grey-60',
    ]"
    data-test-id="vf-textarea"
  >
    <label
      class="relative f-col grow pt-2"
      data-test-id="vf-textarea-label"
    >
      <!-- @slot Slot is meant for `label` only -->
      <slot name="label" v-bind="{ focused }">
        <span
          class="pointer-events-none absolute left-0 flex items-center duration"
          :class="[
            modelValue || focused ? 'text-xs -top-2 bg-white' : 'top-2',
            { 'c-grey-10': focused && !invalid },
          ]"
        >
          <span>
            <span v-if="label" v-bind="propsLabel">{{ label }}</span>
            <span v-if="label && required" aria-hidden="true"> *</span>
          </span>
        </span>
      </slot>
      <!-- @slot Slot is meant for passing `textarea` element for lower level configurations -->
      <span class="flex grow -mx-3" @focusin="focused = true" @focusout="focused = false">
        <slot>
          <base-textarea
            v-bind="{ ...props, ...propsTextarea }"
            ref="textareaRef"
            v-model="modelValue"
            class="w-full bg-transparent pl-3 pr-8 c-grey-10"
            :class="{
              'pr-10': isIconShown || $slots.end,
              'pr-18': isIconShown && $slots.end,
              'resize-y': resizable,
            }"
            :rows
            @blur="$emit('blur', $event)"
            @change="$emit('change', $event)"
            @focus="$emit('focus', $event)"
            @input="$emit('input', $event)"
          />
        </slot>
      </span>
    </label>
    <span class="absolute right-0 top-0 m-3 flex gap-1" data-test-id="vf-textarea-info" @click.prevent>
      <!-- With click.prevent input gets focused instead of tooltip displaying when user clicks the icon -->
      <slot name="after" />
      <vf-icon
        v-if="isIconShown"
        :class="invalid ? 'c-red-30' : 'c-green-30'"
        :name="invalid ? 'error' : 'success'"
        size="md"
      />
    </span>
  </div>
</template>

<script lang="ts" setup>
import type { TextareaHTMLAttributes } from 'vue'

// Import props in textarea components that extend vf-textarea
export type Props = {
  /**
   * Defines disabled state
   */
  disabled?: boolean
  /**
   * Defines validation state
   * There are 3 possible states
   * Indeterminate === undefined (no validation icons visible)
   * Valid === true
   * Invalid === false
   */
  invalid?: boolean
  /**
   * Label is always required for accessibility purposes
   * if you want to hide it, use hideLabel prop
   */
  label?: string
  /**
   * Allows passing additional attributes to the label element
   * E.g. `id` and `class` for accessibility purposes
   */
  propsLabel?: Record<PropertyKey, unknown>
  /**
   * Allows to pass properties to textarea native element
   */
  propsTextarea?: TextareaHTMLAttributes
  /**
   * Defines whether textarea can be resized
   */
  resizable?: boolean
  /**
   * Defines if the textarea is required for accessibility purposes
   */
  required?: boolean
  rows?: number | string
}

// Import Emits in textarea components that extend vf-textarea
export type Emits = {
  blur: [value: string]
  change: [value: string]
  focus: [value: string]
  input: [value: string]
}

// Import Slots in textarea components that extend vf-textarea
export type Slots = {
  after?: () => void
  default?: () => void
  label?: (props: { focused: boolean }) => void
}

const {
  /**
   * Needs to be explicitly set to `undefined` for indeterminate validation state
   */
  invalid = undefined,
  rows = 11,
  label,
  propsTextarea,
  propsLabel,
  ...props
} = defineProps<Props>()

defineEmits<Emits>()

defineSlots<Slots>()

const textareaRef = useTemplateRef('textareaRef')
const modelValue = defineModel<string>()
const focused = ref(false)

const { showErrorIcon, showSuccessIcon } = useAppConfig().components.vf.input

const isIconShown = computed(() => {
  if (showErrorIcon && showSuccessIcon) return invalid !== undefined
  if (showErrorIcon) return invalid === true
  if (showSuccessIcon) return invalid === false
})

defineExpose({
  textareaRef
})
</script>
